import { Token } from "./TokenEnum";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { Address } from "@wagmi/core";
import { getNetwork, readContract } from "wagmi/actions";
import { GlobalStore } from "./GlobalStore";
import * as Sentry from "@sentry/browser";
import { Amount } from "../../types/amount";
import {
  PERMIT_TWO_CONTRACT_ADDRESS,
  SENTRY_CONTEXTS,
  TOKEN_DECIMALS_MAP,
} from "../../config/consts";
import { Chain } from "@rainbow-me/rainbowkit";
import { arbitrum, mainnet, polygon } from "wagmi/chains";
import { BALANCES_ALLOWANCES_CONTRACT_ABI } from "../../config/abis/BalancesAllowancesContract.abi";
import { TokenMetadata } from "./getTokenMetadata";
import { getIsUserConnected } from "utils/userUtils";

export interface Allowance {
  symbol: Token;
  allowance: Amount;
}
export type Allowances = Array<Allowance>;
type ChainId = Chain["id"];
const BALANCE_CONTRACTS: Record<ChainId, `0x${string}`> = {
  [mainnet.id]: "0x21486E4427031F10fBE45F47AAd6f729014d42a4",
  [polygon.id]: "0x132F13C3896eAB218762B9e46F55C9c478905849",
  [arbitrum.id]: "0xf8D3B856cc0A9c99A5b849246b8E679E32298E1c",
};
const fetchAllowances = (
  address: Address,
  tokenMetadata: TokenMetadata | undefined
): Promise<Array<{ symbol: Token; allowance: Amount }>> => {
  const { chain } = getNetwork();
  if (chain && tokenMetadata) {
    const tokenSymbols = Object.keys(tokenMetadata) as Array<Token>;
    const tokensAddresses = tokenSymbols.map(symbol => {
      return tokenMetadata[symbol]?.chainInfo[0].contractAddress;
    }) as Array<`0x${string}`>;
    return readContract({
      address: BALANCE_CONTRACTS[chain.id],
      abi: BALANCES_ALLOWANCES_CONTRACT_ABI,
      functionName: "walletAllowances",
      args: [address, PERMIT_TWO_CONTRACT_ADDRESS, tokensAddresses],
    })
      .then(res => {
        const allowances = res.map((allowanceBigNum, index) => {
          const tokenDecimals = getTokenDecimals(tokenSymbols[index], tokenMetadata);
          const allowance = new Amount(allowanceBigNum.toString()).div(
            new Amount((10 ** tokenDecimals).toString())
          );
          return {
            symbol: tokenSymbols[index],
            allowance: allowance,
          };
        });
        return allowances;
      })
      .catch(err => {
        console.error(`Allowances error`, err);
        return [];
      });
  }
  return Promise.reject("Invalid or no chain");
};

export const getAllowances = createAsyncAction(
  async ({
    address,
    tokenMetadata,
  }: {
    address: `0x${string}`;
    tokenMetadata: TokenMetadata | undefined;
  }) => {
    try {
      Sentry.configureScope(scope => {
        scope.setTransactionName(SENTRY_CONTEXTS.ALLOWANCES);
      });
      const result = await fetchAllowances(address, tokenMetadata);
      if (result && result.length > 0) {
        return successResult(result);
      }
    } catch (e) {
      Sentry.captureException(`Could not get allowances`, { extra: { event: e } });
      return errorResult([], `Could not get allowances`, e);
    }
    Sentry.captureException(`Could not get allowances`);
    return errorResult([], `Couldn't get allowances`);
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error && getIsUserConnected()) {
        GlobalStore.update(s => {
          s.allowances = result.payload;
        });
      }
    },
  }
);

export const getTokenDecimals = (
  symbol: Token,
  tokenMetadata: TokenMetadata | undefined
): number => {
  if (tokenMetadata) {
    const decimals = tokenMetadata[symbol]?.chainInfo[0].decimals;
    if (decimals) {
      return decimals;
    }
  }
  const message = `Could not get decimals for ${symbol}. Falling back to config`;
  console.warn(message);
  Sentry.captureMessage(message);
  const hardcodedDecimals = TOKEN_DECIMALS_MAP[symbol];
  if (hardcodedDecimals === undefined) {
    const warning = `Could not get hardcoded decimals for ${symbol}. Falling back to 18`;
    Sentry.captureMessage(warning);
    const FALLBACK_DECIMALS = 18;
    return FALLBACK_DECIMALS;
  }
  return hardcodedDecimals;
};
